import Aura from '@primeuix/themes/aura';
import type { Preset } from '@primeuix/themes/types';

/**
 * SpyderFlow Theme Configuration
 * Extends the Aura preset with custom design tokens and component overrides
 */

/**
 * Custom theme preset extending Aura
 * This configuration will be used as the base for both light and dark themes
 */
export const spyderFlowThemePreset: Preset = {
	...Aura,
	primitive: {
		...Aura.primitive,
		borderRadius: {
			none: '0',
			xs: '2px',
			sm: '4px',
			md: '6px',
			lg: '8px',
			xl: '12px',
		},
		// You can customize focus ring styles globally
		focusRing: {
			width: '2px',
			style: 'solid',
			color: '{primary.color}',
			offset: '2px',
		},
	},
	semantic: {
		...Aura.semantic,
		colorScheme: {
			light: {
				...Aura.semantic?.colorScheme?.light,
				formField: {
					...Aura.semantic?.colorScheme?.light?.formField,
					background: '{surface.100}',
					filledBackground: '{surface.100}',
				},
			},
			dark: {
				...Aura.semantic?.colorScheme?.dark,
				surface: {
					...Aura.semantic?.colorScheme?.dark?.surface,
					900: '#1E1E1E',
					950: '#121212',
				},
				formField: {
					...Aura.semantic?.colorScheme?.dark?.formField,
					background: '{surface.900}',
					filledBackground: '{surface.900}',
				},
			},
		},
	},
	components: {
		...Aura.components,
		autocomplete: {
			...Aura.components?.autocomplete,
			colorScheme: {
				...Aura.components?.autocomplete?.colorScheme,
				light: {
					...Aura.components?.autocomplete?.colorScheme?.light,
					dropdown: {
						...Aura.components?.autocomplete?.colorScheme?.light?.dropdown,
						background: '{primary.color}',
						color: '{primary.contrast.color}',
						borderColor: '{primary.color}',
					},
				},
				dark: {
					...Aura.components?.autocomplete?.colorScheme?.dark,
					dropdown: {
						...Aura.components?.autocomplete?.colorScheme?.dark?.dropdown,
						background: '{primary.color}',
						color: '{primary.contrast.color}',
						borderColor: '{primary.color}',
					},
				},
			},
		},

		confirmdialog: {
			...Aura.components?.confirmdialog,
			icon: {
				...Aura.components?.confirmdialog?.icon,
				size: '1rem',
			},
		},
		datatable: {
			...Aura.components?.datatable,

			bodyCell: {
				...Aura.components?.datatable?.bodyCell,
				borderColor: '{primary.color}',
				padding: '0.75rem 0.5rem ',
			},
			columnTitle: {
				...Aura.components?.datatable?.columnTitle,
				fontWeight: '500',
			},
			headerCell: {
				...Aura.components?.datatable?.headerCell,
				background: 'color-mix(in srgb, var(--p-primary-color) 45%, transparent)',
				padding: '0.25rem 0.5rem',
				borderColor: '{primary.color}',
			},
			row: {
				...Aura.components?.datatable?.row,
				background: 'color-mix(in srgb, var(--p-primary-color) 8%, transparent)',
			},
		},
		datepicker: {
			...Aura.components?.datepicker,
			dropdown: {
				...Aura.components?.datepicker?.dropdown,
				borderColor: '{primary.color}',
			},
			colorScheme: {
				...Aura.components?.datepicker?.colorScheme,
				light: {
					...Aura.components?.datepicker?.colorScheme?.light,
					dropdown: {
						...Aura.components?.datepicker?.colorScheme?.light?.dropdown,
						background: '{primary.color}',
						hoverBackground: '{primary.600}',
						color: '{primary.contrast.color}',
						hoverColor: '{primary.contrast.color}',
						borderColor: '{primary.color}',
					},
				},
				dark: {
					...Aura.components?.datepicker?.colorScheme?.dark,
					dropdown: {
						...Aura.components?.datepicker?.colorScheme?.dark?.dropdown,
						background: '{primary.color}',
						hoverBackground: '{primary.600}',
						color: '{primary.contrast.color}',
						hoverColor: '{primary.contrast.color}',
						borderColor: '{primary.color}',
					},
				},
			},
		},
		dialog: {
			...Aura.components?.dialog,
			header: {
				...Aura.components?.dialog?.header,
				padding: '1rem 1rem 0.5rem 1rem',
			},
			content: {
				...Aura.components?.dialog?.content,
				padding: '0.5rem 1rem 1rem 1rem',
			},
			footer: {
				...Aura.components?.dialog?.footer,
				padding: '0rem 1rem 1rem 1rem',
			},
		},
		drawer: {
			...Aura.components?.drawer,
			header: {
				...Aura.components?.drawer?.header,
				padding: '1rem 1rem 0.5rem 1rem',
			},
			content: {
				...Aura.components?.drawer?.content,
				padding: '0rem 1rem 1rem 1rem',
			},
		},
		fieldset: {
			...Aura.components?.fieldset,
			colorScheme: {
				...Aura.components?.fieldset?.colorScheme,
				light: {
					...Aura.components?.fieldset?.colorScheme?.light,
					root: {
						...Aura.components?.fieldset?.colorScheme?.light?.root,
						background: '{surface.200}',
					},
				},
				dark: {
					...Aura.components?.fieldset?.colorScheme?.dark,
					root: {
						...Aura.components?.fieldset?.colorScheme?.dark?.root,
						background: '{surface.950}',
					},
				},
			},
			root: {
				...Aura.components?.fieldset?.root,
				padding: '0rem 1rem 1rem 1rem',
			},

			content: {
				...Aura.components?.fieldset?.content,
				padding: '0.5rem 0rem 0rem 0',
			},
		},
		floatlabel: {
			...Aura.components?.floatlabel,
			colorScheme: {
				...Aura.components?.floatlabel?.colorScheme,
				light: {
					...Aura.components?.floatlabel?.colorScheme?.light,
					root: {
						...Aura.components?.floatlabel?.colorScheme?.light?.root,
						focusColor: '{surface.800}',
					},
				},
				dark: {
					...Aura.components?.floatlabel?.colorScheme?.dark,
					root: {
						...Aura.components?.floatlabel?.colorScheme?.dark?.root,
						focusColor: '{surface.100}',
					},
				},
			},
		},
		galleria: {
			...Aura.components?.galleria,
			colorScheme: {
				...Aura.components?.galleria?.colorScheme,
				light: {
					...Aura.components?.galleria?.colorScheme?.light,
					navButton: {
						...Aura.components?.galleria?.colorScheme?.light?.navButton,
						background: 'color-mix(in srgb, {surface.950} 50%, transparent)',
						hoverBackground: 'color-mix(in srgb, {surface.950} 70%, transparent)',
					},
					closeButton: {
						...Aura.components?.galleria?.colorScheme?.light?.closeButton,
						background: 'color-mix(in srgb, {surface.950} 50%, transparent)',
						hoverBackground: 'color-mix(in srgb, {surface.950} 70%, transparent)',
					},
				},
				dark: {
					...Aura.components?.galleria?.colorScheme?.dark,
					navButton: {
						...Aura.components?.galleria?.colorScheme?.dark?.navButton,
						background: 'color-mix(in srgb, {surface.950} 50%, transparent)',
						hoverBackground: 'color-mix(in srgb, {surface.950} 70%, transparent)',
					},
					closeButton: {
						...Aura.components?.galleria?.colorScheme?.dark?.closeButton,
						background: 'color-mix(in srgb, {surface.950} 50%, transparent)',
						hoverBackground: 'color-mix(in srgb, {surface.950} 70%, transparent)',
					},
				},
			},
		},
		image: {
			...Aura.components?.image,
			toolbar: {
				...Aura.components?.image?.toolbar,
				position: {
					...Aura.components?.image?.toolbar?.position,
					top: 'auto',
					right: 'auto',
					bottom: '0.5rem',
				},
			},
			colorScheme: {
				...Aura.components?.image?.colorScheme,
				light: {
					...Aura.components?.image?.colorScheme?.light,
					toolbar: {
						...Aura.components?.image?.colorScheme?.light?.toolbar,
						background: 'color-mix(in srgb, {surface.950} 50%, transparent)',
					},
				},
				dark: {
					...Aura.components?.image?.colorScheme?.dark,
					toolbar: {
						...Aura.components?.image?.colorScheme?.dark?.toolbar,
						background: 'color-mix(in srgb, {surface.950} 50%, transparent)',
					},
				},
			},
		},
		inputtext: {
			...Aura.components?.inputtext,
			colorScheme: {
				...Aura.components?.inputtext?.colorScheme,
				light: {
					...Aura.components?.inputtext?.colorScheme?.light,
					root: {
						...Aura.components?.inputtext?.colorScheme?.light?.root,
						focusBorderColor: '{surface.800}',
					},
				},
				dark: {
					...Aura.components?.inputtext?.colorScheme?.dark,
					root: {
						...Aura.components?.inputtext?.colorScheme?.dark?.root,
						focusBorderColor: '{surface.100}',
					},
				},
			},
		},
		textarea: {
			...Aura.components?.textarea,
			colorScheme: {
				...Aura.components?.textarea?.colorScheme,
				light: {
					...Aura.components?.textarea?.colorScheme?.light,
					root: {
						...Aura.components?.textarea?.colorScheme?.light?.root,
						focusBorderColor: '{surface.800}',
					},
				},
				dark: {
					...Aura.components?.textarea?.colorScheme?.dark,
					root: {
						...Aura.components?.textarea?.colorScheme?.dark?.root,
						focusBorderColor: '{surface.100}',
					},
				},
			},
		},
		listbox: {
			...Aura.components?.listbox,
			root: {
				...Aura.components?.listbox?.root,
				borderRadius: '0',
				shadow: 'none',
				borderColor: 'transparent',
				background: 'transparent',
			},
			option: {
				...Aura.components?.listbox?.option,
				padding: '1rem 0.75rem',
			},
		},
		menubar: {
			...Aura.components?.menubar,
			root: {
				...Aura.components?.menubar?.root,
				borderColor: 'transparent',
				borderRadius: '0',
				padding: '0.5rem 1rem',
			},
			colorScheme: {
				...Aura.components?.menubar?.colorScheme,
				light: {
					...Aura.components?.menubar?.colorScheme?.light,
					root: {
						...Aura.components?.menubar?.colorScheme?.light?.root,
						background: '{surface.200}',
					},
				},
				dark: {
					...Aura.components?.menubar?.colorScheme?.dark,
					root: {
						...Aura.components?.menubar?.colorScheme?.dark?.root,
						background: '{surface.950}',
					},
				},
			},
		},
		multiselect: {
			...Aura.components?.multiselect,
			colorScheme: {
				...Aura.components?.multiselect?.colorScheme,
				light: {
					...Aura.components?.multiselect?.colorScheme?.light,
					root: {
						...Aura.components?.multiselect?.colorScheme?.light?.root,
						focusBorderColor: '{surface.800}',
					},
				},
				dark: {
					...Aura.components?.multiselect?.colorScheme?.dark,
					root: {
						...Aura.components?.multiselect?.colorScheme?.dark?.root,
						focusBorderColor: '{surface.100}',
					},
				},
			},
		},
		popover: {
			...Aura.components?.popover,
			colorScheme: {
				...Aura.components?.popover?.colorScheme,
				light: {
					...Aura.components?.popover?.colorScheme?.light,
					root: {
						...Aura.components?.popover?.colorScheme?.light?.root,
						background: Aura.components?.tooltip?.colorScheme?.light?.root?.background,
					},
				},
				dark: {
					...Aura.components?.popover?.colorScheme?.dark,
					root: {
						...Aura.components?.popover?.colorScheme?.dark?.root,
						background: Aura.components?.tooltip?.colorScheme?.dark?.root?.background,
					},
				},
			},
		},
		progressbar: {
			...Aura.components?.progressbar,
			root: {
				...Aura.components?.progressbar?.root,
				height: '4px',
			},
		},
		tabs: {
			...Aura.components?.tabs,
			tablist: {
				...Aura.components?.tabs?.tablist,
				borderWidth: '0rem 0rem 2px 0',
			},
			tabpanel: {
				...Aura.components?.tabs?.tabpanel,
				padding: '0',
			},
			activeBar: {
				...Aura.components?.tabs?.activeBar,
				height: '2px',
			},

			colorScheme: {
				...Aura.components?.tabs?.colorScheme,
				light: {
					...Aura.components?.tabs?.colorScheme?.light,
					tab: {
						...Aura.components?.tabs?.colorScheme?.light?.tab,
						activeColor: '#000',
					},
				},
				dark: {
					...Aura.components?.tabs?.colorScheme?.dark,
					tab: {
						...Aura.components?.tabs?.colorScheme?.dark?.tab,
						activeColor: '#fff',
					},
				},
			},
		},
		toast: {
			...Aura.components?.toast,
			text: {
				...Aura.components?.toast?.text,
				gap: '0',
			},
		},
		togglebutton: {
			...Aura.components?.togglebutton,
			root: {
				...Aura.components?.togglebutton?.root,
				padding: '0',
			},
			content: {
				...Aura.components?.togglebutton?.content,
				padding: '{button.padding-y} {button.padding-x}',
			},

			colorScheme: {
				...Aura.components?.togglebutton?.colorScheme,
				light: {
					...Aura.components?.togglebutton?.colorScheme?.light,
					content: {
						...Aura.components?.togglebutton?.colorScheme?.light?.content,
						checkedBackground: '{primary.color}',
					},
					root: {
						...Aura.components?.togglebutton?.colorScheme?.light?.root,
						checkedColor: '{primary.contrast.color}',
					},
				},
				dark: {
					...Aura.components?.togglebutton?.colorScheme?.dark,
					content: {
						...Aura.components?.togglebutton?.colorScheme?.dark?.content,
						checkedBackground: '{primary.color}',
					},
					root: {
						...Aura.components?.togglebutton?.colorScheme?.dark?.root,
						checkedColor: '{primary.contrast.color}',
					},
				},
			},
		},
		toolbar: {
			...Aura.components?.toolbar,
			root: {
				...Aura.components?.toolbar?.root,
				background: 'transparent',
				borderColor: 'transparent',
				borderRadius: '{border.radius.md}',
				gap: '0.5rem',
				padding: '0',
			},
		},
		splitter: {
			...Aura.components?.splitter,
			colorScheme: {
				...Aura.components?.splitter?.colorScheme,
				light: {
					...Aura.components?.splitter?.colorScheme?.light,
					gutter: {
						...Aura.components?.splitter?.colorScheme?.light?.gutter,
						background: '{surface.300}',
					},
				},
				dark: {
					...Aura.components?.splitter?.colorScheme?.dark,
					gutter: {
						...Aura.components?.splitter?.colorScheme?.dark?.gutter,
						background: '{surface.700}',
					},
				},
			},
		},
		stepper: {
			...Aura.components?.stepper,
			separator: {
				...Aura.components?.stepper?.separator,
				size: '1px',
				margin: '0 6rem',
			},
			step: {
				...Aura.components?.stepper?.step,
				padding: '0',
			},
			steppanels: {
				...Aura.components?.stepper?.steppanels,
				padding: '1rem',
			},
			colorScheme: {
				light: {
					separator: {
						background: '{content.border.color}',
					},
				},
				dark: {
					separator: {
						background: '{content.border.color}',
					},
				},
			},
		},
	},
};

/**
 * Complete PrimeNG theme configuration
 */
export const primeNGThemeConfig = {
	preset: spyderFlowThemePreset,
	options: {
		prefix: 'p',
		darkModeSelector: '.p-dark', // Use class selector for manual dark mode control
		cssLayer: false,
	},
} as const;
